import { makeWidgetProps } from 'Src/types/widget-helpers';
import type { ProductFeaturesFacility } from 'Src/types/common/productFeatures';
import React, { useMemo } from 'react';
import { connect, useSelector } from 'react-redux';
import {
  updatePriceInfo,
  normalizeCartChildcaresForChildcarePricingInfo,
} from 'Components/utils/booking/booking-additional-services/BookingAddtionalServices.utils';
import { ICONS } from 'Components/utils/icon/icons';
import { updateBookingCart } from 'Components/widgets/cart/cart.actions';
import { hasChildrenAgedXOrLess } from 'Components/widgets/childcare/childcare.helper';
import { useAppContext } from 'Context/ReactAppContext';
import { useChildcareContextPLE } from 'Src/hooks/use-childcare-context/useChildcareContext';
import { t } from 'Src/i18n';
import { selectAttendeesWithInfosByHouseholds } from 'Store/booking/attendees/attendees.selector';
import { addOrRemoveChildcareFromCart } from 'Utils/childcare/childcare.utils';
import { capitalizeWordFirstCaracter } from 'Utils/string/stringUtils';
import { useCartLocked } from 'Utils/ple-utils';
import { Badge } from 'Components/frameworks/badge/Badge';
import { Icon } from 'Components/frameworks/icon/Icon';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer';
import { Title } from 'Components/frameworks/title/Title';
import { Childcare } from 'Components/widgets/childcare/Childcare';
import { ChildcareIncludedEquipmentCapsule } from 'Components/widgets/childcare/ChildcareIncludedEquipmentCapsule';
import { ChildcareOnDemandEquipmentCapsule } from 'Components/widgets/childcare/ChildcareOnDemandEquipmentCapsule';
import { ChildcareContext, type ChildcareContextType } from 'Context/ChildcareContext';
import { ServicesContext, type ServicesContextType } from 'Context/ServicesContext';
type Props = {
  cartBookings: Array<Cart>;
  stay: StayEntity;
  updateCart: (arg0: any) => Promise<any>;
};
export const ChildcareLayer = ({ cartBookings, stay, updateCart }: Props) => {
  const appContext = useAppContext();
  const cartState = useSelector((state) => state.memberSpace.cart.data)?.bookingsCart;
  const { isCartLocked } = useCartLocked(stay.booking.id);
  const childcareAdditionalServices = stay.upsells.childcares;
  const cartBooking = cartBookings.find(({ bookingId }) => bookingId === stay.booking.id);
  const stayService = (cartBooking?.stayServices || []).find(
    ({ stayIndex }) => stayIndex === stay.stayIndex,
  );
  const cartChildcares = stayService?.childcares || [];
  const { attendees: attendeesWithInfos } = useSelector(selectAttendeesWithInfosByHouseholds);
  const attendeesList = [
    ...attendeesWithInfos.adults,
    ...attendeesWithInfos.children.map((child) => ({ ...child, isChild: true })),
  ];
  const kidAttendeesFromCart = (cartBooking?.attendees || [])
    .filter(({ type }) => type === 'KID')
    .map(({ birthday, birthdate, ...attendee }) => ({
      ...attendee,
      birthdate: birthday || birthdate,
    }));
  const kidAttendeesFromUpsells = childcareAdditionalServices
    .map(({ pricingInfo }) => {
      const childAttendee = pricingInfo.attendees?.find(({ id }) => id === pricingInfo.childId);

      if (!pricingInfo.childId || !childAttendee) {
        return null;
      }

      return {
        id: pricingInfo.childId,
        birthdate: childAttendee.birthdate,
        firstName: childAttendee.firstName,
        lastName: childAttendee.lastName,
      };
    })
    .filter((attendee): attendee is NonNullable<typeof attendee> => !!attendee);
  const kidAttendees =
    kidAttendeesFromCart.length > 0 ? kidAttendeesFromCart : kidAttendeesFromUpsells;
  const includedChildrenActivities = stay.product.childrenActivities.filter(
    (activity) => activity.equipments.length > 1,
  );
  const onDemandChildrenActivities = stay.product.childrenActivities.filter(
    (activity) => !activity.isIncluded,
  );
  const shouldShowIncludedEquipments = hasChildrenAgedXOrLess(kidAttendees, 6);
  const childcareAdditionalServicesUpdatedWithCart: ReadonlyArray<UpsellChildcare> = [
    ...childcareAdditionalServices,
  ].map((childcare) => ({
    ...childcare,
    pricingInfo: updatePriceInfo(
      childcare.pricingInfo,
      normalizeCartChildcaresForChildcarePricingInfo(
        cartChildcares,
        childcare.pricingInfo,
        kidAttendees,
      ),
      appContext.locale,
    ),
  }));

  // same for delete or add this only the classic Childcare case ie: one schedule one slot
  const addOrRemoveChildcareCC = ({
    childcareId,
    attendeeId,
    startDate,
    endDate,
  }: {
    childcareId: string;
    attendeeId: string;
    startDate: string;
    endDate: string;
  }) => {
    return addOrRemoveChildcareFromCart({
      childcareCart: cartChildcares.find(({ id }) => id === childcareId),
      attendeeId,
      childcareId,
      startDate,
      endDate,
      bookingId: stay.booking.id,
      updateCart,
      appContext,
    });
  };

  const ServicesContextValuesCC = useMemo(
    () => ({
      attendeesList,
      arrivalDate: stay.dates.startDate,
      departureDate: stay.dates.endDate,
      showRentAllSchedules: true,
    }),
    [attendeesList],
  );
  const { childcareContextValuesPLE } = useChildcareContextPLE({
    cartState,
    stay,
    addOrRemoveChildcareCC,
    cartChildcares,
  });
  return (
    <SiteContainer className="py-20">
      <div className="flex flex-col gap-20 sm:flex-row" data-testid="ChildcareLayer">
        <div className="basis-full sm:basis-2/3 md:basis-3/4">
          <ServicesContext.Provider
            value={makeWidgetProps<ServicesContextType>(ServicesContextValuesCC)}
          >
            <ChildcareContext.Provider
              value={makeWidgetProps<ChildcareContextType>(childcareContextValuesPLE)}
            >
              <Childcare
                childcareAdditionalServices={childcareAdditionalServicesUpdatedWithCart.filter(
                  ({ productInfo }) => !!productInfo,
                )}
                attendees={kidAttendees}
                addChildcare={addOrRemoveChildcareCC}
                deleteChildcare={addOrRemoveChildcareCC}
                isCartLocked={isCartLocked}
                openModalWhenRemove
                shouldTrack={false}
              />
            </ChildcareContext.Provider>
          </ServicesContext.Provider>
        </div>
        <div className="basis-full sm:order-first sm:basis-1/3 md:basis-1/4">
          <Icon className="text-sienna" name={ICONS.PEOPLE_TRIPLE} width="2rem" />
          <Title className="text-sienna" theme="title-brand-n3">
            {t('booking.childcarePopIn.childcareTitle')}
          </Title>
          <p className="mb-40 mt-5 text-14">{t('booking.childcarePopIn.childcareDescription')}</p>
          <div className="mb-40">
            {kidAttendees.map((attendee, i) => {
              const isRented = cartChildcares.some(({ attendees: cartAttendees }) =>
                cartAttendees.some(({ id }) => id === attendee.id),
              );
              const currentAttendeeChildcare = childcareAdditionalServicesUpdatedWithCart.find(
                ({ pricingInfo }) => pricingInfo.childId === attendee.id,
              );
              const currentAttendeeChildcareChild =
                currentAttendeeChildcare?.pricingInfo.attendees.find(
                  ({ id }) => id === attendee.id,
                );
              const alreadyAdded =
                !!currentAttendeeChildcare?.productInfo &&
                currentAttendeeChildcareChild?.schedules?.some(
                  ({ isPartiallyPaid }) => !!isPartiallyPaid,
                );
              return (
                <p
                  key={attendee.id}
                  className="mb-20 flex items-center gap-15 text-16 font-bold text-black"
                >
                  {isRented || alreadyAdded ? (
                    <Badge
                      className="border-success bg-success text-white"
                      iconName={ICONS.CHECK}
                      iconSize="1.5rem"
                      variant="pill"
                    />
                  ) : (
                    <Badge
                      className="border-saffron bg-saffron font-bold text-black"
                      label={String(i + 1)}
                      variant="pill"
                    />
                  )}
                  {t('common.fullNameOrder', {
                    firstName:
                      attendee.unicodeFirstname?.trim() ||
                      capitalizeWordFirstCaracter(
                        attendee.firstName?.trim() || attendee.firstname?.trim() || '',
                      ),
                    lastName:
                      attendee.unicodeLastname?.trim() ||
                      capitalizeWordFirstCaracter(
                        attendee.lastName?.trim() || attendee.lastname?.trim() || '',
                      ),
                  })}
                </p>
              );
            })}
          </div>

          {shouldShowIncludedEquipments && (
            <ChildcareIncludedEquipmentCapsule
              className="mb-20"
              facilities={
                [...includedChildrenActivities, ...stay.product.facilities] as Array<
                  Partial<ProductFacility> & ProductFeaturesFacility
                >
              }
            />
          )}
          <ChildcareOnDemandEquipmentCapsule
            isSeller={appContext.isSeller}
            activities={onDemandChildrenActivities}
          />
        </div>
      </div>
    </SiteContainer>
  );
};

const mapStateToProps = (state) => {
  const cartBookings = state.memberSpace.cart.data?.bookingsCart?.cartBookings || [];
  return {
    cartBookings,
  };
};

const mapDispatchToProps = {
  updateCart: updateBookingCart,
};
export const ChildcareLayerConnected = connect(mapStateToProps, mapDispatchToProps)(ChildcareLayer);
